import { Box, Dialog, DialogActions, DialogContent, Typography } from "@mui/material"
import { WarningOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"

import { Button } from "@/components/common/Button"
import type { GoodsReceipt, Product } from "@/graphql/codegen/graphql"
import { formatWarehouseLabel } from "@/utils/warehouse"

interface CostBreakdownIncompleteDialogProps {
  goodsReceipt: GoodsReceipt
  products: Product[]
  onClose: () => void
}

export const CostBreakdownIncompleteDialog: FC<CostBreakdownIncompleteDialogProps> = ({
  goodsReceipt,
  products,
  onClose,
}) => (
  <Dialog open onClose={onClose} data-testid='cost-breakdown.incomplete-dialog'>
    <DialogContent classes={{ root: "p-10" }}>
      <Box className='mb-6 w-full justify-center text-center'>
        <WarningOutlined className='size-12 text-secondary-600' />
        <Typography
          data-testid='cost-breakdown.incomplete-dialog.title'
          variant='h5'
          color='primary'
          className='mt-6 font-normal'
          component='h2'
        >
          Cost Breakdown Not Completed
        </Typography>
      </Box>
      <Box
        className='mt-6 px-3 text-center text-base leading-[150%] tracking-[-0.85px] text-gray-700'
        data-testid='cost-breakdown.incomplete-dialog.content'
      >
        <p className='mb-4'>
          The Cost Breakdown details were saved, but the following product(s) could not be completed or synced with
          AS400 because they are not associated with{" "}
          <b>{goodsReceipt.warehouseArea && formatWarehouseLabel(goodsReceipt.warehouseArea)}</b>:
        </p>
        <ul className='list-disc px-4 text-left font-normal'>
          {products.map((product, index) => (
            <li key={product.productId} data-testid={`cost-breakdown.incomplete-dialog.product-${index}`}>
              {product.companyProductDescription}
            </li>
          ))}
        </ul>
        <p className='mt-4'>
          Please associate the warehouse under Product Details and retry Cost Breakdown completion.
        </p>
      </Box>
    </DialogContent>
    <DialogActions className='justify-end px-10 pb-10 pt-0'>
      <Button
        appearance='contained'
        color='primary'
        onClick={onClose}
        data-testid='cost-breakdown.incomplete-dialog.submit-button'
      >
        OK
      </Button>
    </DialogActions>
  </Dialog>
)
